import type { Injector } from '@angular/core';
import { DateUtility, listTaskEvidenceStates, TASK_EVIDENCE_OUTCOME_EXPLANATIONS, TaskEvidenceOutcome, type Task, type TaskEvidenceKind } from '@jms/model';
import { evidenceLevelsSummary } from '@jms/ui/ui.evidence.enforcement.control';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { SURFACE_LOCALE } from '@workbuddy/ui/v2/surface.engine';
import type { EntityBoardListParentProgressResolver, EntityBoardListPropertyIconBadge, EntityBoardListPropertyPresentation, EntityBoardListPropertyResolver, EntityBoardListPropertyTone, EntityBoardListTitleAdornmentResolver } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { buildTaskEvidenceCard, type EvidenceDateFormatter, type TaskEvidenceCard } from './evidence.exception/evidence.hover.content';
import { TASK_EVIDENCE_OUTCOME_TONES } from './evidence.exception/task.evidence.outcome.tone';

// Task-specific list projections registered by the manifest: evidence, due date, mandatory state, and child progress.

const evidenceIcons: Record<TaskEvidenceKind, string> = {
  signature: 'fa-solid fa-signature',
  comment: 'fa-regular fa-comment',
  beforePhoto: 'fa-regular fa-camera',
  duringPhoto: 'fa-regular fa-camera',
  afterPhoto: 'fa-regular fa-camera',
  form: 'fa-regular fa-clipboard-list',
  file: 'fa-solid fa-paperclip',
};

export const resolveTaskEvidenceListProperty: EntityBoardListPropertyResolver = ({ item, injector }): EntityBoardListPropertyPresentation | null => {

  const task = item as Task;
  const states = listTaskEvidenceStates(task);
  if (!states.length) return null;

  // The Evidence filter reads only the stored outcome, so a chip computed without one showed Captured on tasks the filter cannot find (WBS-12452).
  if (!task.evidenceOutcome) return null;

  const icon = evidenceIcons[(states.find(state => !state.captured) ?? states[0]).kind];
  const card = buildTaskEvidenceCard(task.evidenceOutcome, task, recordedOnFormatter(injector));

  return {
    label: card.title,
    tooltip: describeEvidenceOutcome(task, task.evidenceOutcome, card),
    // An outcome with no details of its own states the levels in its hover card.
    hoverCard: card.rows.length ? card : { title: card.title, rows: [{ value: evidenceLevelsSummary(task) }] },
    iconLeft: icon,
    tone: TASK_EVIDENCE_OUTCOME_TONES[task.evidenceOutcome],
    // The tones' borders differ in contrast, so some outcomes looked outlined and others not (Cong 08/10).
    borderless: true,
    dashed: false,
  };
};

const evidenceIconBadges: Partial<Record<TaskEvidenceOutcome, EntityBoardListPropertyIconBadge>> = {
  [TaskEvidenceOutcome.Captured]: 'success',
  [TaskEvidenceOutcome.Exception]: 'error',
};

/** Evidence for the one-line v2 list row (WBS-13010): the evidence icon with a corner badge for its outcome; no badge means not captured. */
export const resolveTaskEvidenceListIcon: EntityBoardListPropertyResolver = (args): EntityBoardListPropertyPresentation | null => {

  const chip = resolveTaskEvidenceListProperty(args);
  const outcome = (args.item as Task).evidenceOutcome;
  // Only optional evidence: nothing is owed, so the row stays quiet.
  if (!chip || outcome === TaskEvidenceOutcome.NoneRequired) return null;

  return {
    ...chip,
    iconOnly: true,
    // A task closed without completing can no longer capture anything.
    iconLeft: outcome === TaskEvidenceOutcome.NotApplicable ? 'fa-regular fa-file-slash' : chip.iconLeft,
    iconBadge: outcome ? evidenceIconBadges[outcome] : undefined,
  };
};

/** Due chip dated in the zone a mount names for the task (its stage site), else the general setting zone, else UTC; the board's own Date rule paints the UTC day, a day early east of UTC (WBS-12244). */
export function resolveTaskDueDateListProperty(zoneFor?: (task: Task) => string): EntityBoardListPropertyResolver {

  return ({ item, field, injector }) => {
    const task = item as Task;
    if (!task.dueDate) return null;
    const zone = zoneFor?.(task) ?? injector?.get(GeneralSettingService, null)?.generalSettings?.region?.timezone ?? 'UTC';
    const due = DateUtility.getDateTime(task.dueDate, zone).startOf('day');
    if (!due.isValid) return null;
    const days = Math.round(due.diff(DateUtility.getDateTime(new Date(), zone).startOf('day'), 'days').days);
    const label = dueDayLabel(days) ?? due.toFormat('MMM d');
    return {
      label,
      tooltip: `${field.displayName ?? 'Due Date'}: ${label}`,
      iconLeft: 'fa-regular fa-calendar',
      tone: dueDayTone(days),
    };
  };
}

function dueDayLabel(days: number): string | null {

  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return null;
}

// Same thresholds as the board's default due chip, so a task reads like any other due date.
function dueDayTone(days: number): EntityBoardListPropertyTone {

  if (days < 0) return 'error';
  if (days <= 7) return 'warning';
  return 'neutral';
}

const dates = new DateTimeFormatPipe();

// Same zone, region format and fallbacks as task details, so the exception reads the same time on both (WBS-12550).
function recordedOnFormatter(injector: Injector | undefined): EvidenceDateFormatter {

  const region = injector?.get(GeneralSettingService, null)?.generalSettings?.region;
  const format = `${region?.dateFormat ?? 'dd/MM/yyyy'} ${region?.timeFormat ?? 'HH:mm'}`;
  const timezone = injector?.get(SURFACE_LOCALE, null)?.timezone();

  return value => dates.transform(value, timezone, format);
}

// The text form stays for the overflow summary, which joins chip tooltips as plain text.
function describeEvidenceOutcome(task: Task, outcome: TaskEvidenceOutcome, card: TaskEvidenceCard): string {

  if (TASK_EVIDENCE_OUTCOME_EXPLANATIONS[outcome]) return card.rows[0].value;
  if (!card.rows.length) return `${evidenceLevelsSummary(task)}: ${card.title}`;
  if (outcome === TaskEvidenceOutcome.Exception) return [card.title, ...card.rows.map(row => `${row.label}: ${row.value}`)].join('. ');

  return `Still missing: ${card.rows.map(row => row.value).join(', ')}`;
}

export const resolveTaskTitleAdornment: EntityBoardListTitleAdornmentResolver = (item) => {

  const task = item as Task;
  if (task.mandatory !== true && task.mandatoryForClaiming !== true) return null;
  return { icon: 'fa-solid fa-asterisk', label: 'Mandatory', tone: 'error' };
};

export const resolveTaskParentProgress: EntityBoardListParentProgressResolver = (item) => {

  const progress = (item as Task).childProgress;
  if (!progress || progress.total < 1) return null;
  return progress;
};

/** A Daily parent only groups its day instances; each instance carries the assignee, so the parent row shows none. */
export const resolveTaskAssigneeListProperty: EntityBoardListPropertyResolver = ({ item }) => {

  const task = item as Task;
  if (task.daily && !task.instance) return null;
  return undefined;
};
